import { Browser } from '@capacitor/browser';
import { isRevenueCatConfigured, ensureRevenueCatIdentity } from '@/config/revenuecat';

/**
 * Play Billing purchase flow via RevenueCat, for the four purchase entry
 * points gated to this provider by utils/purchaseAvailability. Mirrors
 * stripeCheckout.ts's role: a thin platform bridge, not business logic.
 *
 * Deliberately does not touch the Firestore profile doc on success — same
 * rule as Stripe. Only the webhook (Admin SDK) may write tier/subscription
 * fields; the client shows a generic "processing" state and lets the
 * Firestore listener pick up the change once the webhook lands, typically
 * within a few seconds.
 */

export type PurchaseOutcome =
  | { status: 'success' }
  | { status: 'cancelled' }
  | { status: 'error'; message: string };

/**
 * The package to purchase for the app's one subscription. RevenueCat models
 * multiple offerings/packages for apps with several plans; we have exactly
 * one (monthly), so this collapses that generality down to "the current
 * offering's monthly package, or null if RevenueCat has nothing configured
 * yet" (e.g. Play Console linkage isn't finished — see PLAY_BILLING_PLAN.md).
 */
export async function getPremiumPackage() {
  if (!isRevenueCatConfigured()) return null;
  const { Purchases } = await import('@revenuecat/purchases-capacitor');
  const offerings = await Purchases.getOfferings();
  return offerings.current?.monthly ?? null;
}

export async function purchasePremium(): Promise<PurchaseOutcome> {
  if (!isRevenueCatConfigured()) {
    return { status: 'error', message: 'Purchases are not available right now.' };
  }
  const { Purchases, PURCHASES_ERROR_CODE } = await import('@revenuecat/purchases-capacitor');
  try {
    // Refuse to charge anyone under an identity the webhook could not
    // attribute — the money would leave their account and the entitlement
    // would never arrive. Throws if the sign-in-time link never succeeded.
    await ensureRevenueCatIdentity();
    const aPackage = await getPremiumPackage();
    if (!aPackage) {
      return { status: 'error', message: 'Premium is not available right now. Please try again shortly.' };
    }
    await Purchases.purchasePackage({ aPackage });
    return { status: 'success' };
  } catch (err: unknown) {
    const code = (err as { code?: string } | null)?.code;
    if (code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
      return { status: 'cancelled' };
    }
    console.error('[PlayBilling] purchase failed:', err);
    const message = (err as { message?: string } | null)?.message;
    return { status: 'error', message: message || 'Something went wrong. Please try again.' };
  }
}

export async function restorePurchases(): Promise<PurchaseOutcome> {
  if (!isRevenueCatConfigured()) {
    return { status: 'error', message: 'Restore is not available right now.' };
  }
  try {
    await ensureRevenueCatIdentity();
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const { customerInfo } = await Purchases.restorePurchases();
    const active = Object.keys(customerInfo.entitlements.active).length > 0;
    return active
      ? { status: 'success' }
      : { status: 'error', message: 'No previous purchase found on this Google account.' };
  } catch (err: unknown) {
    console.error('[PlayBilling] restore failed:', err);
    const message = (err as { message?: string } | null)?.message;
    return { status: 'error', message: message || 'Something went wrong. Please try again.' };
  }
}

/**
 * Opens Google Play's own subscription management page — the platform's
 * native surface for cancelling/changing payment method, not an external
 * payment flow, so this is fine to link to under Play's anti-steering rules
 * (unlike the Stripe portal link on native, which utils/purchaseAvailability
 * keeps hidden for exactly that reason).
 */
export async function openManageSubscription(): Promise<void> {
  if (!isRevenueCatConfigured()) return;
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const { customerInfo } = await Purchases.getCustomerInfo();
    const url = customerInfo.managementURL
      || 'https://play.google.com/store/account/subscriptions';
    await Browser.open({ url });
  } catch (err: unknown) {
    console.error('[PlayBilling] open manage subscription failed:', err);
    await Browser.open({ url: 'https://play.google.com/store/account/subscriptions' }).catch(() => {});
  }
}
